// lib
import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { DataContext } from '@aytm/app/stats/page/question-views/components/questions-mapping/Component'

import { getMethodOptions } from '@aytm/app/stats/page/question-views/components/questions-mapping/lib'
import { generateUID, encodeText, decodeText } from '@aytm/app/stats/lib/util/helpers'

// components
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'

import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

// hooks
import useSingleAndDoubleClick from '@aytm/app/stats/page/components/hooks/useSingleAndDoubleClick'

import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/components/questions-mapping/styles/Component.module.scss'

const Attribute = ({ item, position, dimension, dndItemsList, onMethodFocus, onMethodBlur }) => {
  const { id, mode, data, disabled, attributesOptions = [], dispatch } = useContext(DataContext)

  const [methodsType, setMethodsType] = useState(null)
  const [methodsSubtype, setMethodsSubtype] = useState(null)

  const [selectedValue, setSelectedValue] = useState(null)

  const [itemMode, setItemMode] = useState(mode)

  const [input, setInput] = useState(item.refs)
  const [focused, setFocused] = useState(false)

  const timer = useRef(null)

  const changedRef = useRef(false)

  const dndListItemRef = useRef()
  const dndListItemHandleRef = useRef()

  const refsRef = useRef(null)
  refsRef.current = item.refs

  const focusedRef = useRef(focused)
  focusedRef.current = focused

  const methodRef = useRef(null)
  const methodValueRef = useRef(item.method)

  const methodOptionsResult = useMemo(() => {
    const refs = item.refs
      .split(',')
      .map(v => v.trim())
      .slice(0, dimension)

    return getMethodOptions(refs, data.questions)
  }, [item.refs, dimension, data.questions])

  const methodsOptions = methodOptionsResult.methods

  // Derive type/subtype state in an effect — the memo above stays side-effect-free
  useEffect(() => {
    setMethodsType(methodOptionsResult.type)
    setMethodsSubtype(methodOptionsResult.subtype)
  }, [methodOptionsResult])

  const onMethodChange = useCallback(
    (methods, applyToAll = false) => {
      let method = methods
      if (Array.isArray(methods)) {
        method.sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)))
        method = method.join(',')
      }

      if (applyToAll)
        dispatch({
          type: 'UPDATE_ATTRIBUTES_METHOD',
          data: {
            method: method
          }
        })
      else
        dispatch({
          type: 'UPDATE_ATTRIBUTE',
          data: {
            item: {
              ...item,
              method: method
            },
            position: position - 1
          }
        })
    },
    [item, position, dispatch]
  )

  const fetchData = useCallback(() => {
    return {
      id,
      item,
      position: position - 1,
      windowUID: PageGlobals.WINDOW_UID,
      remove: () =>
        dispatch({
          type: 'DELETE_ATTRIBUTE',
          data: position - 1
        })
    }
  }, [item, position, id, dispatch])

  const dndListItem = ADND.targets.itemsList.listItem({
    type: `dlq-attribute-${PageGlobals.WINDOW_UID}`,
    list: dndItemsList
  })
  const dndListItemHandle = ADND.sources
    .listItemHandle({
      type: `dlq-attribute-${PageGlobals.WINDOW_UID}`,
      item: dndListItem,
      cloneable: true
    })
    .fetchData(fetchData)

  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
    ) : (
      <span className={option.item ? '' : 'p-item-label'}>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
      </span>
    )
  const valueTemplate = (option, _props) =>
    option ? (
      <>
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.label || option.text }}></span>
      </>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
    )

  const buttonLabelTemplate = event => {
    const methods =
      event != undefined && event.value != undefined && 0 < event.value.length
        ? methodsOptions.filter(o => event.value.includes(o.key))
        : []

    const text =
      0 < methods.length
        ? methods.length == methodsOptions.length
          ? 'All'
          : 1 == methods.length
          ? methods[0].label
          : methods
              .map(o => o.key)
              .join(',')
              .toUpperCase()
        : 'Select...'
    return <span dangerouslySetInnerHTML={{ __html: text }}></span>
  }

  const onInputChange = event => setInput(event.target.value || '')

  const onInputFocus = () => setFocused(true)
  const onInputBlur = () => setFocused(false)

  const onSelectedChange = value => {
    if (value === refsRef.current) return

    const option = attributesOptions.find(v => v.key == value)

    changedRef.current = true

    dispatch({
      type: 'UPDATE_ATTRIBUTE',
      data: {
        item: {
          ...item,
          text: option ? option.label : item.text,
          refs: value || ''
        },
        position: position - 1
      }
    })
  }

  const onLabelChange = event => {
    dispatch({
      type: 'UPDATE_ATTRIBUTE',
      data: {
        item: {
          ...item,
          text: encodeText(event.target.value)
        },
        position: position - 1
      }
    })
  }

  const onDeleteClick = () => {
    dispatch({
      type: 'DELETE_ATTRIBUTE',
      data: position - 1
    })
  }

  const onHandleClick = useSingleAndDoubleClick(null, () => {
    dispatch({
      type: 'ADD_ATTRIBUTE',
      data: {
        item: {
          ...item,
          key: generateUID()
        },
        position: position
      }
    })
  })

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    timer.current && clearTimeout(timer.current)

    focusedRef.current &&
      (timer.current = setTimeout(() => {
        onSelectedChange(input)
      }, 500))

    return () => timer.current && clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input])

  useEffect(() => {
    if (changedRef.current) {
      dispatch({
        type: 'UPDATE_ATTRIBUTE',
        data: {
          item: {
            ...item,
            method: 0 < methodsOptions.length ? methodsOptions[0].key : ''
          },
          position: position - 1
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodsType, methodsSubtype])

  useEffect(() => {
    dispatch({
      type: 'UPDATE_ERRORS',
      data: {
        id: `a${position}`,
        error:
          null === item.method || !item.method.length || !item.refs.split(',').filter(v => 0 < v.trim().length).length
      }
    })
  }, [item.method, item.refs, position, dispatch])

  useEffect(() => {
    timer.current && clearTimeout(timer.current)

    const refs = item.refs
      ? item.refs
          .split(',')
          .map(v => v.trim())
          .join(', ')
      : ''

    setSelectedValue(item.refs ? refs : null)

    if (!focusedRef.current) setInput(refs)

    changedRef.current = false
  }, [item.refs])

  useEffect(() => {
    if (!methodsOptions.length) {
      if (null !== item.method && 0 < item.method.length) onMethodChange('')
    } else {
      // the type state lags these options by a commit, so read it from the same result
      const { type, subtype } = methodOptionsResult
      if (['rb', 'mx', 'sr', 'se'].includes(type) || ('mp' == type && 'radios' == subtype)) {
        if (!methodsOptions.some(option => option.key === item.method)) {
          onMethodChange(
            methodsOptions.some(option => option.key === methodValueRef.current)
              ? methodValueRef.current
              : methodsOptions[0].key
          )
        } else methodValueRef.current = item.method
      } else {
        if (0 < (item.method || '').length) {
          const methods = (item.method || '').split(',')
          if (methods.some(method => !methodsOptions.some(option => option.key === method))) {
            onMethodChange(
              (methodValueRef.current || '')
                .split(',')
                .some(method => methodsOptions.some(option => option.key === method))
                ? (methodValueRef.current || '')
                    .split(',')
                    .filter(method => methodsOptions.some(option => option.key === method))
                    .join(',')
                : methodsOptions.map(option => option.key).join(',')
            )
          } else methodValueRef.current = item.method
        } else methodValueRef.current = item.method
      }
    }
  }, [item.method, methodsOptions, methodOptionsResult, onMethodChange])

  useEffect(() => {
    const isAdvanced =
      'advanced' == mode ||
      !attributesOptions.length ||
      (null !== selectedValue && !attributesOptions.some(v => v.key == selectedValue))

    setItemMode(isAdvanced ? 'advanced' : mode)
  }, [mode, attributesOptions, selectedValue])

  const renderAdvanced = () => (
    <InputText
      className={classNames('p-inputtext-rounded p-composite-right', {
        'p-inputtext-empty': !input.length,
        'p-invalid': 0 < input.length && !focused && !methodsOptions.length
      })}
      placeholder={'Please type question numbers (ex: "Q1, Q3, Q5...")'}
      value={input}
      disabled={disabled}
      onChange={disabled ? null : onInputChange}
      onFocus={disabled ? null : onInputFocus}
      onBlur={disabled ? null : onInputBlur}
    />
  )
  const renderBasic = () => (
    <Dropdown
      className="p-dropdown-rounded p-composite-right"
      placeholder="Select..."
      value={selectedValue}
      options={attributesOptions}
      optionValue="key"
      optionLabel="text"
      itemTemplate={itemTemplate}
      valueTemplate={valueTemplate}
      filter
      filterBy="search,text"
      filterPlaceholder="Search items"
      resetFilterOnHide
      filterInputAutoFocus={false}
      fixedDropdownDir="bottom"
      disabled={disabled}
      onChange={disabled ? null : event => onSelectedChange(event.value)}
    />
  )

  const renderComponent = () => (
    <>
      {/* keyed by position: the list's drag handlers rewrite this cell's text outside React, so a new
          position mounts a fresh node rather than updating text that is no longer on screen */}
      <div key={position} className={style.index}>
        {position}.
      </div>
      <div
        className={classNames(style.group, style['group-av'], 'p-col', {
          [style['group-av-disabled']]: disabled
        })}
      >
        <div className="p-fluid p-col-6">{disabled || 'advanced' == itemMode ? renderAdvanced() : renderBasic()}</div>
        <div ref={methodRef} className={`${style.group} ${style['group-composite']} p-col-6`}>
          {0 < methodsOptions.length ? (
            ['rb', 'mx', 'sr', 'se'].includes(methodsType) || ('mp' == methodsType && 'radios' == methodsSubtype) ? (
              <div className="p-fluid p-col-5">
                <Dropdown
                  data-testid={`stat-dl-attribute-method-${position}`}
                  className="p-dropdown-rounded p-composite"
                  placeholder="Select..."
                  value={item.method}
                  options={methodsOptions}
                  optionValue="key"
                  optionLabel="text"
                  itemTemplate={itemTemplate}
                  valueTemplate={valueTemplate}
                  appendTo={methodRef.current || 'self'}
                  fixedDropdownDir="bottom"
                  disabled={disabled}
                  onChange={
                    disabled
                      ? null
                      : event =>
                          onMethodChange(
                            event.value,
                            event.originalEvent &&
                              event.originalEvent.shiftKey &&
                              !event.originalEvent.ctrlKey &&
                              !event.originalEvent.altKey &&
                              !event.originalEvent.metaKey
                          )
                  }
                  onFocus={onMethodFocus}
                  onBlur={onMethodBlur}
                />
              </div>
            ) : (
              <div className="p-fluid p-col-5">
                <MultiSelect
                  data-testid={`stat-dl-attribute-method-${position}`}
                  className="p-multiselect-rounded p-composite"
                  placeholder="Select..."
                  value={0 < item.method.length ? item.method.split(',') : []}
                  options={methodsOptions}
                  optionValue="key"
                  optionLabel="text"
                  itemTemplate={itemTemplate}
                  buttonLabelTemplate={buttonLabelTemplate}
                  panelHeaderTemplate={MultiSelectHeader}
                  panelFooterTemplate={MultiSelectFooter}
                  appendTo={methodRef.current || 'self'}
                  fixedDropdownDir="bottom"
                  disabled={disabled}
                  onChange={
                    disabled
                      ? null
                      : event =>
                          onMethodChange(
                            event.value,
                            event.originalEvent &&
                              event.originalEvent.shiftKey &&
                              !event.originalEvent.ctrlKey &&
                              !event.originalEvent.altKey &&
                              !event.originalEvent.metaKey
                          )
                  }
                  onFocus={onMethodFocus}
                  onBlur={onMethodBlur}
                />
              </div>
            )
          ) : (
            <div className="p-fluid p-col-5">
              <MultiSelect
                data-testid={`stat-dl-attribute-method-${position}`}
                className="p-multiselect-rounded p-composite"
                placeholder="Select..."
                appendTo={methodRef.current || 'self'}
                fixedDropdownDir="bottom"
                disabled
              />
            </div>
          )}
          <div className="p-fluid p-col">
            <InputText
              className={classNames('p-inputtext-rounded', {
                'p-composite': !disabled,
                'p-composite-left': disabled
              })}
              placeholder="Please type a label..."
              value={decodeText(item.text)}
              maxLength={120}
              disabled={disabled}
              onChange={disabled ? null : onLabelChange}
            />
          </div>
        </div>
      </div>
      {disabled ? null : (
        <span ref={dndListItemHandleRef} className={style.handle} onClick={disabled ? null : onHandleClick} />
      )}
    </>
  )

  return (
    <div ref={dndListItemRef} className={`PRAYTMTheme ${style.row} ${style['row-av']}`}>
      {disabled ? null : (
        <span className={style.delete} aria-label="Delete attribute" {...pressable(disabled ? null : onDeleteClick)}>
          <i className="icon-aytmfd-x-v2" />
        </span>
      )}
      <Popup
        targetClassName="p-fluid p-col-12"
        width="254px"
        trigger={0 < item.refs.length && !methodsOptions.length ? 'hover' : 'none'}
        direction="topleft"
        popupOffset={46}
        show={0 < item.refs.length && !methodsOptions.length ? null : false}
        hideOtherPopupsOnShow
        content={() => <p>Please list corresponding questions/subquestions for each run separated by commas.</p>}
      >
        {renderComponent()}
      </Popup>
    </div>
  )
}

Attribute.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number,
  dimension: PropTypes.number,
  dndItemsList: PropTypes.any,
  onMethodFocus: PropTypes.func,
  onMethodBlur: PropTypes.func
}

Attribute.displayName = 'Attribute'

export default Attribute
